import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FieldSelector } from '../../cart';
import Cta from '../../Cta';
import RadioButton from '../../RadioButton';
import { getConfig, translate } from '@micro-apps/env';
import ErrorMessage from '../ErrorMessage';
import { getCountries, validateInputType } from '@micro-apps/shared';

const { RESERVATIONS_SITE_URL } = getConfig(process.env);

interface RequestOTPFormProps {
	locale: string;
	isPersistentlySignedIn: boolean;
	sendCodeTo: string;
	loginType: string;
	isError: boolean;
	errorMessage: any;
	profile: any;
	handleRequest: () => void;
	setSendCodeTo: (sendCodeTo: string) => void;
	setLoginType: (loginType: string) => void;
	setIsError: (isError: boolean) => void;
}

const RequestOTPForm: React.FC<RequestOTPFormProps> = ({
	locale,
	isPersistentlySignedIn,
	sendCodeTo,
	loginType,
	isError,
	errorMessage,
	profile,
	handleRequest,
	setSendCodeTo,
	setLoginType,
	setIsError,
}) => {
	const inputOptions = [
		{
			name: 'sendCodeVia',
			value: 'EMAIL_ADDRESS',
			label: translate('Email me'),
		},
		{
			name: 'sendCodeVia',
			value: 'PHONE_NUMBER',
			label: translate('Text me'),
		},
	];

	const getContactInfo = (items, defaultProp, valueProp) => {
		const defaultItem = items?.find(item => item[defaultProp]);
		return defaultItem?.[valueProp] || items?.[0]?.[valueProp] || '';
	};

	const sendToEmail = getContactInfo(profile?.emails, 'defaultEmail', 'email');
	const sendToPhone = getContactInfo(profile?.phones, 'defaultPhone', 'number');
	const [countries, setCountries] = useState([]);
	const [invalidError, setInvalidError] = useState('');

	const currentErrorMessage =
		invalidError ||
		(isError
			? loginType !== 'EMAIL_ADDRESS'
				? translate('Please enter a valid phone number')
				: translate('Please enter a valid email')
			: '');

	const getErrorCodeForTranslations = errorCode => {
		if (!errorCode) {
			return 'There was a problem processing your request. Please contact us for assistance.';
		}
		if (!errorCode.startsWith('E')) {
			return errorCode;
		}

		return `error.${errorCode}`;
	};

	useEffect(() => {
		setSendCodeTo(loginType === 'EMAIL_ADDRESS' ? sendToEmail : sendToPhone);
	}, [loginType]);

	useEffect(() => {
		const fetchCountries = async () => {
			const fetchedCountries = await getCountries();
			setCountries(fetchedCountries);
		};

		fetchCountries();
	}, [locale]);

	return (
		<>
			<h1 className="heading-2">{translate('Sign In')}</h1>
			<p className="intro body-2">
				{translate('View and edit your Four Seasons profile and customise your preferences.')}
			</p>
			<form
				className={classNames('request-otp body-2', { isError: isError })}
				onSubmit={evt => {
					evt.preventDefault();
					if (sendCodeTo !== '') {
						handleRequest();
					}
				}}
			>
				<div className="radio-inputs">
					<RadioButton
						id={'signup-options'}
						options={inputOptions}
						onChange={evt => {
							setIsError(false);
							setSendCodeTo('');
							setLoginType(evt.target.value);
						}}
						className="body-2"
					/>
				</div>
				{isError && (
					<ErrorMessage
						message={`${translate(getErrorCodeForTranslations(errorMessage), [sendCodeTo])}`}
					/>
				)}
				{isPersistentlySignedIn ? (
					<p className="body-2 persistent-login">{sendCodeTo}</p>
				) : (
					<FieldSelector
						type={loginType !== 'EMAIL_ADDRESS' ? 'PHONE' : 'EMAIL'}
						value={sendCodeTo}
						name="sendCodeTo"
						readOnly={isPersistentlySignedIn}
						countriesDialCode={countries}
						onChange={e => {
							const errorMessage = validateInputType({
								inputType: loginType !== 'EMAIL_ADDRESS' ? 'PHONE' : 'EMAIL',
								value: e.target.value,
								min: 1,
							});
							errorMessage ? setInvalidError(errorMessage) : setInvalidError('');
							isError && setIsError(false);
							setSendCodeTo(e.target.value);
						}}
						field=""
						form=""
						styles=""
						content=""
						label={translate(loginType === 'EMAIL_ADDRESS' ? 'Email' : 'Phone number') + '*'}
						errorMessage={currentErrorMessage}
					/>
				)}
				{loginType === 'PHONE_NUMBER' && (
					<p className="labels-caption details-caption">
						{translate(
							'If you choose to receive your password via mobile, we will send one text message per login attempt. Message and data rates may apply.'
						)}
					</p>
				)}
				<Cta
					type="primary"
					disabled={sendCodeTo === ''}
					onClick={() => !invalidError && handleRequest()}
					className={classNames('button-filled button', {
						'button--margin': loginType === 'EMAIL_ADDRESS' && currentErrorMessage,
					})}
				>
					{translate('Next')}
				</Cta>
			</form>
			<div>
				<p className="register body-2">
					{translate('Don’t have an account?')}{' '}
					<a
						target="_blank"
						rel="noopener noreferrer"
						href={`${RESERVATIONS_SITE_URL}${locale !== 'en' ? '/' + locale : ''}/register`}
					>
						{translate('Register')}
					</a>
				</p>

				{isError && (
					<div>
						<div className="call-us body-2">
							<p>{translate('If you are having problems signing in, please call us.')}.</p>
							<div className="body-2 other-toll-fee">
								<a href="tel:+18008195053">+1 (800) 819-5053</a>{' '}
								<a href="/global-toll-free-telephone-numbers/">
									{translate('Other Toll-Free Numbers')}
								</a>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default RequestOTPForm;
